import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/components/AppLayout';
import { taxApi, type Tax, type TaxTypeChoice } from '@/lib/api';
import { ArrowLeft, Pencil } from 'lucide-react';

function EditTaxContent() {
  const { nav } = useBusinessNav();
  const { id } = useParams<{ id: string }>();
  const { currentBusiness } = useApp();
  const [loading, setLoading] = useState(true);
  const [taxTypeChoices, setTaxTypeChoices] = useState<TaxTypeChoice[]>([]);
  const [tax, setTax] = useState<Tax | null>(null);

  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [taxType, setTaxType] = useState('exclusive');
  const [applyToAllParts, setApplyToAllParts] = useState(false);
  const [applyToAllLabor, setApplyToAllLabor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    taxApi.getTaxTypes().then((response) => {
      if (response.data) {
        setTaxTypeChoices(response.data);
      }
    });

    if (id) {
      taxApi.get(id).then((response) => {
        setLoading(false);
        if (response.data) {
          const t = response.data;
          setTax(t);
          setName(t.name);
          setRate(t.rate);
          setTaxType(t.tax_type);
          setApplyToAllParts(t.apply_to_all_parts);
          setApplyToAllLabor(t.apply_to_all_labor);
        } else if (response.error) {
          setError('Tax not found');
        }
      });
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBusiness || !id) return;

    setError('');
    setSubmitting(true);

    // Note: rate and tax_type are not included because they're immutable after creation
    const data = {
      name,
      apply_to_all_parts: applyToAllParts,
      apply_to_all_labor: applyToAllLabor,
      business: currentBusiness.id,
    };

    const response = await taxApi.update(id, data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    nav('/taxes');
  };

  if (!tax && !loading) {
    return (
      <div className="px-6 pt-6 pb-6">
        <p className="text-destructive">Tax not found</p>
        <Button onClick={() => nav('/taxes')} className="mt-4">
          Back to Taxes
        </Button>
      </div>
    );
  }

  if (loading) {
    return null;
  }

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav('/taxes')}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tax Rates
        </Button>
      </div>

      <div className="space-y-1 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Pencil className="h-6 w-6" />
          Edit Tax
        </h1>
        <p className="text-sm text-muted-foreground">
          Update this tax rate's settings.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Tax Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Sales Tax, VAT, GST"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate">Tax Rate (%)</Label>
              <Input
                id="rate"
                type="text"
                value={`${rate}%`}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Tax rates cannot be changed after creation. If you need a different rate, archive this tax and create a new one.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxType">Tax Type</Label>
              <Select value={taxType} disabled>
                <SelectTrigger className="bg-muted">
                  <SelectValue placeholder="Select tax type" />
                </SelectTrigger>
                <SelectContent>
                  {taxTypeChoices.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Tax type cannot be changed after creation. If you need a different type, archive this tax and create a new one.
              </p>
            </div>

            <div className="space-y-3">
              <Label>Auto-apply this tax</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applyToAllParts"
                    checked={applyToAllParts}
                    onCheckedChange={(checked) => setApplyToAllParts(checked === true)}
                  />
                  <Label htmlFor="applyToAllParts" className="font-normal cursor-pointer">
                    Apply to all parts/products by default
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applyToAllLabor"
                    checked={applyToAllLabor}
                    onCheckedChange={(checked) => setApplyToAllLabor(checked === true)}
                  />
                  <Label htmlFor="applyToAllLabor" className="font-normal cursor-pointer">
                    Apply to all labor by default
                  </Label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, this tax will be automatically applied to new items of the selected type
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => nav('/taxes')} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={submitting || !name}>
                {submitting ? 'Updating...' : 'Update Tax'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function EditTax() {
  return <EditTaxContent />;
}
